import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../shared/_TBComponents/Icon';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import AthleteResultsTable from './AthleteResultsTable';
import { fetchWorkoutResults } from '../../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: #ffffff;
  border-radius: 4px;
  padding: 16px;
  gap: 16px;
  box-shadow: 0 16px 24px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  border: 1px solid #eee;

  @media screen and (max-width: 1200px) {
    min-height: 280px;
  }
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const MenuContainer = styled.div`
  position: relative;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  margin-top: -4px;
  opacity: ${(props) => (props.disabled ? '0.4' : '1')};
  rotate: 90deg;

  &:hover {
    background: ${(props) => (props.disabled ? 'none' : '#f5f5f5')};
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 40px;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 12px;
  z-index: 10;
  min-width: 220px;
`;

const MenuLabel = styled.div`
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 700;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const ToggleContainer = styled.div`
  display: flex;
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
`;

const ToggleOption = styled.button`
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 600;
  padding: 8px 12px;
  border: none;
  background: ${(props) => (props.active ? '#000' : '#fff')};
  color: ${(props) => (props.active ? '#fff' : '#666')};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex: 1;

  &:hover {
    background: ${(props) => (props.active ? '#000' : '#f5f5f5')};
  }

  &:not(:last-child) {
    border-right: 1px solid #d8d8d8;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  justify-content: center;
  height: 85%;
`;

const EmptyState = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 50px;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #EEE;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const AthleteResults = ({ selectedAthlete, targetDate, scrollContainerRef }) => {
  const dispatch = useDispatch();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const workoutResults = useSelector((state) => state.navigator.data.workoutResults);
  const workoutResultsError = useSelector((state) => state.navigator.data.workoutResultsError);
  const isLoadingWorkoutResults = useSelector(
    (state) => state.navigator.ui.isLoadingWorkoutResults,
  );

  const [dateFilterType, setDateFilterType] = useState('assigned');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (selectedAthlete && accountCode) {
      const useAssignedDate = dateFilterType === 'assigned';
      dispatch(fetchWorkoutResults(accountCode, selectedAthlete.id, useAssignedDate));
    }
  }, [selectedAthlete?.id, accountCode, dateFilterType, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const hasData = workoutResults
    && workoutResults.dates
    && workoutResults.dates.length > 0;

  const renderContent = () => {
    if (isLoadingWorkoutResults) {
      return (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      );
    }

    if (workoutResultsError) {
      const errorMessage = typeof workoutResultsError === 'string'
        ? workoutResultsError
        : workoutResultsError.message || workoutResultsError.error || 'Unable to load workout results';
      return (
        <EmptyState>
          <EmptyStateIcon>
            <Icon
              size={24}
              icon='pr-tracking'
              color='#9E9E9E'
            />
          </EmptyStateIcon>
          <Text styledTextProps={{
            fontSize: '16px', fontWeight: 700, marginBottom: '8px',
          }}
          >
            Error loading workout results
          </Text>
          <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
            {errorMessage}
          </Text>
        </EmptyState>
      );
    }

    if (!workoutResults || !workoutResults.dates || workoutResults.dates.length === 0) {
      return (
        <EmptyStateWrapper>
          <EmptyState>
            <EmptyStateIcon>
              <Icon
                size={24}
                icon='pr-tracking'
                color='#9E9E9E'
              />
            </EmptyStateIcon>
            <Text styledTextProps={{
              fontSize: '16px', color: '#9E9E9E', marginBottom: '8px',
            }}
            >
              No workout results available.
            </Text>
            <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
              This athlete has not completed any workouts yet.
            </Text>
          </EmptyState>
        </EmptyStateWrapper>
      );
    }

    return (
      <AthleteResultsTable
        workoutResults={workoutResults}
        targetDate={targetDate}
        scrollContainerRef={scrollContainerRef}
        athleteData={selectedAthlete}
      />
    );
  };

  return (
    <Container>
      <TableHeader>
        <HeaderContent>
          <Text styledTextProps={{
            fontSize: '18px',
            fontWeight: 700,
            letterSpacing: '0.5px',
          }}
          >
            Results
          </Text>
          <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
            View assigned vs completed data by date.
          </Text>
        </HeaderContent>
        {hasData && (
          <MenuContainer ref={menuRef}>
            <MenuButton
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              type='button'
              aria-label='Results options'
            >
              <Icon
                icon='dots'
                size={20}
                color='#9E9E9E'
              />
            </MenuButton>
            {isMenuOpen && (
              <MenuDropdown>
                <MenuLabel>Group by Date</MenuLabel>
                <ToggleContainer>
                  <ToggleOption
                    active={dateFilterType === 'assigned'}
                    onClick={() => setDateFilterType('assigned')}
                    type='button'
                  >
                    Assigned
                  </ToggleOption>
                  <ToggleOption
                    active={dateFilterType === 'completed'}
                    onClick={() => setDateFilterType('completed')}
                    type='button'
                  >
                    Completed
                  </ToggleOption>
                </ToggleContainer>
              </MenuDropdown>
            )}
          </MenuContainer>
        )}
      </TableHeader>
      {renderContent()}
    </Container>
  );
};

AthleteResults.propTypes = {
  selectedAthlete: PropTypes.shape({
    id: PropTypes.number.isRequired,
    first: PropTypes.string.isRequired,
    last: PropTypes.string.isRequired,
  }),
  targetDate: PropTypes.string,
  scrollContainerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

AthleteResults.defaultProps = {
  selectedAthlete: null,
  targetDate: null,
  scrollContainerRef: null,
};

export default AthleteResults;
